import React, { FC } from 'react';
import { Helmet } from 'react-helmet';

import getOfficeSlug from '@helpers/getOfficeSlug';

const daysRuToEn = {
  Понедельник: 'Monday',
  Вторник: 'Tuesday',
  Среда: 'Wednesday',
  Четверг: 'Thursday',
  Пятница: 'Friday',
  Суббота: 'Saturday',
  Воскресенье: 'Sunday',
};

type MetaDataProps = {
  office: OfficeTypeProps;
};

const MetaData: FC<MetaDataProps> = ({ office }) => {
  if (!office) {
    return null;
  }

  const { address, phoneNumber, name, schedule, id } = office;

  const titleStations = '';
  const descrStations = '';

  /*
  if (undergrounds.length > 0) {
    const stations = undergrounds.map(item => item.name).join(', ');

    titleStations = `, метро: ${stations}`;
    descrStations = ` Метро: ${stations}.`;
  }
*/

  const rName = name.replace(/центр госуслуг района\s/i, '');
  const sName = rName.toLowerCase();
  const slug = getOfficeSlug(office);

  const { serializeSchedules } = schedule;
  let scheduleDescription = '';
  let schedulesForLdJson = [];

  if (serializeSchedules) {
    const scheduleByTime = serializeSchedules.reduce((accum, scheduleData) => {
      const { daysOfWeekFull, workDescription } = scheduleData;
      const result = { ...accum };

      if (!result[workDescription]) {
        result[workDescription] = [daysOfWeekFull];
      } else {
        result[workDescription].push(daysOfWeekFull);
      }

      return result;
    }, {});

    const timeVariants = Object.keys(scheduleByTime);

    if (timeVariants.length === 1) {
      scheduleDescription = `Понедельник - Воскресенье, ${timeVariants[0]}`;
    } else {
      scheduleDescription = timeVariants
        .map((variant) => `${scheduleByTime[variant].join(', ')} - ${variant}`)
        .join('; ');
    }

    schedulesForLdJson = timeVariants.map((variant) => {
      const timeSplit = variant.split('–');

      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: scheduleByTime[variant].map((day) => daysRuToEn[day]),
        opens: timeSplit[0],
        closes: timeSplit[1],
      };
    });
  }

  const description = `${name}. Адрес: ${address.value}.${descrStations} Телефон для справок: ${phoneNumber}. Часы работы: ${scheduleDescription}.`;

  // const normalRating = (rating / 20).toFixed(1);

  return (
    <Helmet>
      <title>{`${name}: график работы, телефон, адрес${titleStations}.`}</title>
      <meta name="description" content={description} />
      <meta
        name="keywords"
        content={`мфц ${sName}, мфц ${sName} москвы, мфц ${sName} адрес, мфц ${sName} часы работы, мфц ${sName} как добраться`}
      />
      <meta property="og:title" content={name} />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={`https://www.mos.ru/open-graph/v1/frontend/services/centry-gosudarstvennyh-uslug/${id}/`}
      />
      <meta
        property="og:url"
        content={`https://www.mos.ru/services/centry-gosudarstvennyh-uslug/${slug}`}
      />

      <script type="application/ld+json">
        {`{
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
               "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Услуги",
                    "item": "https://www.mos.ru/services/"
                 },
                 {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Центры государственных услуг",
                    "item": "https://www.mos.ru/services/centry-gosudarstvennyh-uslug/"
                 },
                 {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "${name}",
                    "item": "https://www.mos.ru/services/centry-gosudarstvennyh-uslug/${slug}/"
                 }
               ]
            }`}
      </script>

      <script type="application/ld+json">
        {`{
              "@context": "https://schema.org",
              "@type": "GovernmentOffice",
              "image": [
                "https://www.mos.ru/open-graph/v1/frontend/services/centry-gosudarstvennyh-uslug/${id}/"
              ],
              "@id": "https://www.mos.ru/services/centry-gosudarstvennyh-uslug/${slug}/",
              "name": "${name}",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "${address.value}",
                "addressLocality": "Москва",
                "addressRegion": "${rName}",
                "addressCountry": "RU"
              },

              "url": "https://www.mos.ru/services/centry-gosudarstvennyh-uslug/${slug}/",
              "telephone": "${phoneNumber}",
              "openingHoursSpecification": ${JSON.stringify(schedulesForLdJson)}
           }`}
      </script>
    </Helmet>
  );
};

export default MetaData;
